import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Upload, Tag, Wallet, ShieldCheck, Truck, BadgeCheck, ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { ProductCard } from "@/components/ProductCard";
import { Layout } from "@/components/Layout";

const HERO = "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Landing() {
  const { brand, lang } = useApp();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/products?sort=newest").then(({ data }) => setProducts((data.items || data).slice(0, 8))).catch(() => {});
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  const steps = [
    { icon: Camera, t: "FOTO", d: "Ambil foto barang & bagian detail minusnya secara jujur." },
    { icon: Upload, t: "UPLOAD", d: "Pasang harga, kondisi barang, dan informasi kelengkapan." },
    { icon: Tag, t: "JUAL", d: "Terima tawaran menarik atau langsung dibeli oleh pembeli." },
    { icon: Wallet, t: "TERIMA HASIL", d: "Dana hasil penjualan diproses ke rekening toko terdaftarmu." },
  ];
  const trust = [
    { icon: BadgeCheck, t: "Identitas Seller Terverifikasi", d: "Setiap penjual melalui verifikasi KYC/KYB." },
    { icon: ShieldCheck, t: "Pengalaman Aman", d: "Moderasi produk & perlindungan transaksi." },
    { icon: Truck, t: "Order & Pengiriman", d: "Kelola pesanan dan pelacakan dengan mudah." },
  ];

  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-rl-bg2">
        <div className="rl-hero-grain absolute inset-0 opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rl-fade-up">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-rl-border px-3 py-1 text-xs font-semibold text-rl-secondary">
              <Sparkles className="w-3.5 h-3.5" /> {brand?.tagline_en || "Love It Again"}
            </span>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-rl-ink leading-[1.05]">
              {brand?.tagline || "Barang Bagus Layak Dicintai Lagi."}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-rl-ink2 max-w-lg">
              {brand?.sub_tagline || "Jual yang sudah tidak kamu pakai. Temukan yang ingin kamu miliki."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/belanja" data-testid="hero-shop-cta"
                className="inline-flex items-center gap-2 rounded-full bg-rl-primary hover:bg-rl-primaryHover text-white font-bold px-7 py-3.5 transition-all hover:-translate-y-0.5">
                MULAI BELANJA <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/buka-toko" data-testid="hero-sell-cta"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-rl-border hover:border-rl-secondary text-rl-ink font-bold px-7 py-3.5 transition-all hover:-translate-y-0.5">
                BUKA TOKO SEKARANG
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-rl-muted">
              <div><span className="font-bold text-rl-ink text-lg">1000+</span> Preloved</div>
              <div><span className="font-bold text-rl-ink text-lg">100%</span> Seller Terverifikasi</div>
            </div>
          </div>
          <div className="relative rl-fade-up" style={{ animationDelay: "0.1s" }}>
            <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-rl-border shadow-2xl">
              <img src={HERO} alt="Preloved marketplace" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl border border-rl-border shadow-lg p-4 max-w-[200px] hidden sm:block">
              <div className="flex items-center gap-2 text-rl-secondary"><BadgeCheck className="w-5 h-5" /><span className="font-bold text-sm">Verified Seller</span></div>
              <p className="text-xs text-rl-muted mt-1">Belanja preloved dengan tenang & aman.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-rl-ink">Jelajahi Kategori</h2>
          <Link to="/belanja" className="text-sm font-semibold text-rl-primary hover:underline">Lihat semua</Link>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {cats.map((c) => (
            <Link key={c.id} to={`/belanja?category=${encodeURIComponent(c.name_en)}`}
              data-testid={`category-chip-${c.slug}`}
              className="shrink-0 rounded-full bg-white border border-rl-border hover:border-rl-primary hover:text-rl-primary px-5 py-2.5 text-sm font-semibold text-rl-ink2 transition-colors">
              {lang === "id" ? c.name : c.name_en}
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-6">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-rl-ink">Preloved Pilihan</h2>
          <Link to="/belanja" className="text-sm font-semibold text-rl-primary hover:underline">Lihat semua</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {products.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </section>

      {/* SELLER ACQUISITION */}
      <section className="mt-16 bg-rl-secondary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-24">
          <div className="max-w-2xl">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold">Punya Barang yang Sudah Tidak Dipakai?</h2>
            <p className="mt-4 text-white/80 text-lg">Ubah isi lemari, koleksi, atau barang yang tidak lagi digunakan menjadi peluang penjualan.</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div key={s.t} className="rounded-2xl bg-white/10 border border-white/15 p-6 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-rl-primary grid place-items-center"><s.icon className="w-5 h-5" /></div>
                  <span className="text-sm font-bold text-white/60">0{i + 1}</span>
                </div>
                <h3 className="mt-4 font-bold text-lg tracking-wide">{s.t}</h3>
                <p className="mt-2 text-sm text-white/75">{s.d}</p>
              </div>
            ))}
          </div>
          <Link to="/buka-toko" data-testid="seller-acquisition-cta"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-rl-primary hover:bg-rl-primaryHover text-white font-bold px-8 py-4 transition-all hover:-translate-y-0.5">
            BUKA TOKO SEKARANG <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-20">
        <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-rl-ink text-center">Marketplace Preloved yang Aman & Terpercaya</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {trust.map((f) => (
            <div key={f.t} className="rounded-2xl bg-white border border-rl-border p-7 text-center hover:-translate-y-1 transition-transform">
              <div className="w-14 h-14 rounded-2xl bg-rl-primary/10 text-rl-primary grid place-items-center mx-auto"><f.icon className="w-7 h-7" /></div>
              <h3 className="mt-5 font-bold text-lg text-rl-ink">{f.t}</h3>
              <p className="mt-2 text-sm text-rl-ink2">{f.d}</p>
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
}
